import React, { FunctionComponent, useMemo } from 'react';
import { AttachFile, Close, Done } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { useDocumentUploadState } from '../../hooks/useDocumentUploadState';
import { ApplicationAttachment } from '../../types/applicationAttachment';
import { StoredFile } from '../../types/sponsorshipApplication';
import FileInput, { DEFAULT_ACCEPTED_FILE_EXTENSIONS } from '../input/FileInput';

interface DocumentInputProps {
  doc: ApplicationAttachment;
  isReadonly: boolean;
  fieldErrorHighlightEnabled: boolean;
  documentFiles: Record<string, StoredFile | File | null>;
  handleFilesChange: (newFile: File | null) => void;
  isRequired?: boolean;
  error?: string;
}

const DocumentInput: FunctionComponent<DocumentInputProps> = ({
  doc,
  isReadonly,
  fieldErrorHighlightEnabled,
  documentFiles,
  handleFilesChange,
  isRequired = false,
  error,
}) => {
  const DEFAULT_HELPER_TEXT = 'Maximum file size is 200 MB.';
  const { isUploaded } = useDocumentUploadState(doc.id);

  const allowedExtensions
    = (doc?.document_type?.allowed_extensions || DEFAULT_ACCEPTED_FILE_EXTENSIONS)
      .replace(/\./g, '')
      .replace(/,/g, ', ');

  const defaultHelperText = (
    <>
      {`${DEFAULT_HELPER_TEXT} Allowed file type(s): ${allowedExtensions}`}
      <br />
      {doc.helper_text}
    </>
  );

  const hasError = useMemo(() => !!error || (fieldErrorHighlightEnabled && isRequired && !documentFiles[doc.id]), [
    error,
    fieldErrorHighlightEnabled,
    isRequired,
    doc.id,
    documentFiles,
  ]);

  return (
    <Box key={doc.id}>
      <Typography color='secondary' fontSize={24} fontWeight={500} mb={2} sx={{ color: '#444444' }}>
        {doc.display_name} {isRequired && '*'}
      </Typography>
      <FileInput
        InputProps={
          {
            startAdornment: isUploaded ? <Done color='success' /> : <AttachFile />,
          }
        }
        clearIconButtonProps={
          {
            children: <Close fontSize='small' />,
          }
        }
        disabled={isReadonly}
        error={hasError}
        helperText={error || defaultHelperText}
        id={doc.id}
        inputProps={
          {
            ...(doc.document_type.allowed_extensions && { accept: doc.document_type.allowed_extensions }),
          }
        }
        onChange={handleFilesChange}
        onClick={
          isReadonly && documentFiles[doc.id]
            ? () => window.open((documentFiles[doc.id] as StoredFile)?.url, '_blank')
            : undefined
        }
        placeholder='Insert a file'
        value={documentFiles[doc.id]}
      />
    </Box>
  );
};

export default DocumentInput;
